import React, { useEffect, useRef } from "react";

function GoogleMapsAutocomplete({ onPlaceSelected }) {
  const containerRef = useRef(null);
  const pacRef = useRef(null);

  // Map Google's addressComponents to your fields
  const toAddressFields = (place) => {
    console.log(`[GoogleAutoComplete] Converting place to address fields:`, place);
    const comps = place.addressComponents || [];
    const byType = (t) =>
      comps.find(c => (c.types || []).includes(t)) || {};

    const streetNumber = (byType("street_number").shortText || byType("street_number").longText || "").trim();
    const route = (byType("route").shortText || byType("route").longText || "").trim();

    const city =
      (byType("locality").longText) ||
      (byType("sublocality").longText) ||
      (byType("postal_town").longText) || "";

    const stateValue = byType("administrative_area_level_1").shortText || byType("administrative_area_level_1").longText || "";
    const country = byType("country").shortText || byType("country").longText || "";
    const zipCode = byType("postal_code").longText || "";
    const aptUnit = byType("subpremise").longText || "";

    const addressData = {
      streetNumber: streetNumber || "",
      streetName: route || "",
      city: city || "",
      state: stateValue || "",
      zipCode: zipCode || "",
      country: country || "",
      aptUnit: aptUnit || "",
      line1: [streetNumber, route].filter(Boolean).join(" ").trim(),
      formattedAddress: place.formattedAddress || "",
      lat: place.location?.lat || place.location?.latitude || place.location?.latLng?.lat,
      lng: place.location?.lng || place.location?.longitude || place.location?.latLng?.lng,
      placeId: place.id || place.placeId || ""
    };

    console.log(`[GoogleAutoComplete] Converted address fields:`, addressData);
    return addressData;
  };

  useEffect(() => {
    console.log(`[GoogleAutoComplete] useEffect triggered, checking Google API...`);
    if (!window.google?.maps?.places?.PlaceAutocompleteElement) {
      console.warn(`[GoogleAutoComplete] Google Places API not available`);
      return;
    }

    console.log(`[GoogleAutoComplete] Creating PlaceAutocompleteElement...`);
    const pac = new window.google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ["us"],
    });

    // Apply styling - force light mode colors  
    pac.style.cssText = `
      margin-top: 0.25rem;
      display: block;
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid #d1d5db;
      border-radius: 0.125rem;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      font-size: 0.875rem;
      line-height: 1.25rem;
      outline: none;
      background-color: #ffffff !important;
      color: #374151 !important;
      color-scheme: light !important;
    `;

    // Focus styles
    pac.addEventListener('focus', () => {
      pac.style.borderColor = '#6366f1';
      pac.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
    });

    pac.addEventListener('blur', () => {
      pac.style.borderColor = '#d1d5db';
      pac.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
    });

    // Handle selection events
    const onSelect = async (evt) => {
      console.log(`[GoogleAutoComplete] gmp-select event fired:`, evt);
      try {
        const place = evt.placePrediction.toPlace();
        console.log(`[GoogleAutoComplete] Converting placePrediction to place:`, place);
        
        await place.fetchFields({
          fields: ["addressComponents", "formattedAddress", "location", "id", "displayName"],
        });
        console.log(`[GoogleAutoComplete] Fetched place fields:`, place);
        
        const addressData = toAddressFields(place);
        console.log(`[GoogleAutoComplete] Calling onPlaceSelected with data:`, addressData);
        
        onPlaceSelected?.({ 
          raw: place.toJSON ? place.toJSON() : place, 
          parsed: addressData 
        });
      } catch (e) {
        console.error(`[GoogleAutoComplete] fetchFields failed:`, e);
      }
    };

    pac.addEventListener("gmp-select", onSelect);
    pacRef.current = pac;

    if (containerRef.current) {
      containerRef.current.appendChild(pac);
      console.log(`[GoogleAutoComplete] Element added to DOM container`);
    } else {
      console.error(`[GoogleAutoComplete] Container ref is null!`);
    }

    return () => {
      if (containerRef.current && pac.parentNode) {
        containerRef.current.removeChild(pac);
      }
      if (pacRef.current) {
        pacRef.current.removeEventListener("gmp-select", onSelect);
      }
    };
  }, [onPlaceSelected]);

  return (
    <div>
      <div ref={containerRef}></div>
    </div>
  );
}

export default GoogleMapsAutocomplete;
